import type { ResourceDefinition, ResourceField } from '../types';
import { generateResourceId } from './resourceStore';

// ==========================================
// Types
// ==========================================

export interface ResourceTemplate {
  id: string;
  name: string;
  description: string;
  category: string;
  color: string;
  fields: Array<{
    key: string;
    value: string | number;
    type: 'currency' | 'number' | 'text';
    unit?: string;
    interval?: 'monthly' | 'yearly' | 'hourly';
    aggregate: 'sum' | 'avg' | 'min' | 'max' | 'none';
    showOnBadge: boolean;
  }>;
}

export interface CatalogGroup {
  id: string;
  label: string;
  children?: CatalogGroup[];
  templates?: ResourceTemplate[];
}

export interface CatalogProvider {
  id: string;
  name: string;
  color: string;
  icon?: string;
  groups: CatalogGroup[];
}

// ==========================================
// Helper to build server templates
// ==========================================

function cloudServer(id: string, name: string, cost: number, vcpu: number, ram: number, disk: number, color: string): ResourceTemplate {
  return {
    id, name, category: 'Cloud Server', color,
    description: `${vcpu} vCPU, ${ram} GB RAM, ${disk} GB SSD`,
    fields: [
      { key: 'Kosten', value: cost, type: 'currency', unit: '\u20ac', interval: 'monthly', aggregate: 'sum', showOnBadge: true },
      { key: 'vCPU', value: vcpu, type: 'number', aggregate: 'sum', showOnBadge: false },
      { key: 'RAM GB', value: ram, type: 'number', unit: 'GB', aggregate: 'sum', showOnBadge: false },
      { key: 'Disk GB', value: disk, type: 'number', unit: 'GB', aggregate: 'sum', showOnBadge: false },
    ],
  };
}

function dedicatedServer(id: string, name: string, cost: number, cpu: string, ram: number, disk: string, color: string): ResourceTemplate {
  return {
    id, name, category: 'Dedicated Server', color,
    description: `${cpu}, ${ram} GB RAM, ${disk}`,
    fields: [
      { key: 'Kosten', value: cost, type: 'currency', unit: '\u20ac', interval: 'monthly', aggregate: 'sum', showOnBadge: true },
      { key: 'CPU', value: cpu, type: 'text', aggregate: 'none', showOnBadge: false },
      { key: 'RAM GB', value: ram, type: 'number', unit: 'GB', aggregate: 'sum', showOnBadge: false },
      { key: 'Disk', value: disk, type: 'text', aggregate: 'none', showOnBadge: false },
    ],
  };
}

function gpuServer(id: string, name: string, cost: number, cpu: string, ram: number, disk: string, gpu: string, vram: number, color: string): ResourceTemplate {
  return {
    id, name, category: 'Dedicated Server', color,
    description: `${cpu}, ${ram} GB RAM, ${gpu}, ${vram} GB VRAM`,
    fields: [
      { key: 'Kosten', value: cost, type: 'currency', unit: '\u20ac', interval: 'monthly', aggregate: 'sum', showOnBadge: true },
      { key: 'CPU', value: cpu, type: 'text', aggregate: 'none', showOnBadge: false },
      { key: 'RAM GB', value: ram, type: 'number', unit: 'GB', aggregate: 'sum', showOnBadge: false },
      { key: 'Disk', value: disk, type: 'text', aggregate: 'none', showOnBadge: false },
      { key: 'GPU', value: gpu, type: 'text', aggregate: 'none', showOnBadge: false },
      { key: 'VRAM GB', value: vram, type: 'number', unit: 'GB', aggregate: 'sum', showOnBadge: false },
    ],
  };
}

function loadBalancer(id: string, name: string, cost: number, targets: number, services: number, color: string): ResourceTemplate {
  return {
    id, name, category: 'Cloud Server', color,
    description: `${targets} Targets, ${services} Services`,
    fields: [
      { key: 'Kosten', value: cost, type: 'currency', unit: '\u20ac', interval: 'monthly', aggregate: 'sum', showOnBadge: true },
      { key: 'Targets', value: targets, type: 'number', aggregate: 'sum', showOnBadge: false },
      { key: 'Services', value: services, type: 'number', aggregate: 'sum', showOnBadge: false },
    ],
  };
}

function managedServer(id: string, name: string, cost: number, vcpu: number, ram: number, disk: number, color: string): ResourceTemplate {
  return {
    id, name, category: 'Managed Server', color,
    description: `${vcpu} vCPU, ${ram} GB RAM, ${disk} GB SSD`,
    fields: [
      { key: 'Kosten', value: cost, type: 'currency', unit: '\u20ac', interval: 'monthly', aggregate: 'sum', showOnBadge: true },
      { key: 'vCPU', value: vcpu, type: 'number', aggregate: 'sum', showOnBadge: false },
      { key: 'RAM GB', value: ram, type: 'number', unit: 'GB', aggregate: 'sum', showOnBadge: false },
      { key: 'Disk GB', value: disk, type: 'number', unit: 'GB', aggregate: 'sum', showOnBadge: false },
    ],
  };
}

function storageProduct(id: string, name: string, cost: number, storageTB: number, description: string, color: string): ResourceTemplate {
  return {
    id, name, category: 'Storage', color,
    description,
    fields: [
      { key: 'Kosten', value: cost, type: 'currency', unit: '\u20ac', interval: 'monthly', aggregate: 'sum', showOnBadge: true },
      { key: 'Storage TB', value: storageTB, type: 'number', unit: 'TB', aggregate: 'sum', showOnBadge: false },
    ],
  };
}

// ==========================================
// Hetzner Catalog
// ==========================================

const HETZNER_COLOR = '#d50c2d';

export const RESOURCE_CATALOG: CatalogProvider[] = [
  {
    id: 'hetzner',
    name: 'Hetzner',
    color: HETZNER_COLOR,
    groups: [
      {
        id: 'cloud',
        label: 'Cloud Server',
        children: [
          {
            id: 'arm',
            label: 'Shared vCPU (ARM)',
            templates: [
              cloudServer('hetzner-cax11', 'CAX11', 3.79, 2, 4, 40, HETZNER_COLOR),
              cloudServer('hetzner-cax21', 'CAX21', 6.49, 4, 8, 80, HETZNER_COLOR),
              cloudServer('hetzner-cax31', 'CAX31', 12.49, 8, 16, 160, HETZNER_COLOR),
              cloudServer('hetzner-cax41', 'CAX41', 24.49, 16, 32, 320, HETZNER_COLOR),
            ],
          },
          {
            id: 'amd-shared',
            label: 'Shared vCPU (AMD)',
            templates: [
              cloudServer('hetzner-cpx11', 'CPX11', 4.49, 2, 2, 40, HETZNER_COLOR),
              cloudServer('hetzner-cpx21', 'CPX21', 8.49, 3, 4, 80, HETZNER_COLOR),
              cloudServer('hetzner-cpx31', 'CPX31', 15.49, 4, 8, 160, HETZNER_COLOR),
              cloudServer('hetzner-cpx41', 'CPX41', 28.49, 8, 16, 240, HETZNER_COLOR),
              cloudServer('hetzner-cpx51', 'CPX51', 62.49, 16, 32, 360, HETZNER_COLOR),
            ],
          },
          {
            id: 'amd-dedicated',
            label: 'Dedicated vCPU (AMD)',
            templates: [
              cloudServer('hetzner-ccx13', 'CCX13', 10.49, 2, 8, 80, HETZNER_COLOR),
              cloudServer('hetzner-ccx23', 'CCX23', 18.49, 4, 16, 160, HETZNER_COLOR),
              cloudServer('hetzner-ccx33', 'CCX33', 34.49, 8, 32, 240, HETZNER_COLOR),
              cloudServer('hetzner-ccx43', 'CCX43', 66.49, 16, 64, 360, HETZNER_COLOR),
              cloudServer('hetzner-ccx53', 'CCX53', 130.49, 32, 128, 600, HETZNER_COLOR),
              cloudServer('hetzner-ccx63', 'CCX63', 258.49, 48, 192, 960, HETZNER_COLOR),
            ],
          },
          {
            id: 'lb',
            label: 'Load Balancer',
            templates: [
              loadBalancer('hetzner-lb11', 'LB11', 5.49, 25, 5, HETZNER_COLOR),
              loadBalancer('hetzner-lb21', 'LB21', 15.49, 75, 10, HETZNER_COLOR),
              loadBalancer('hetzner-lb31', 'LB31', 30.49, 150, 25, HETZNER_COLOR),
            ],
          },
        ],
      },
      {
        id: 'dedicated',
        label: 'Dedicated Server',
        children: [
          {
            id: 'ax',
            label: 'AX-Line (AMD)',
            templates: [
              dedicatedServer('hetzner-ax41', 'AX41-NVMe', 44.90, 'AMD Ryzen 5 3600', 64, '2x 512 GB NVMe', HETZNER_COLOR),
              dedicatedServer('hetzner-ax42', 'AX42-U', 54.90, 'AMD Ryzen 7 PRO', 64, '2x 1 TB NVMe', HETZNER_COLOR),
              dedicatedServer('hetzner-ax102', 'AX102-U', 89.90, 'AMD EPYC 9124', 128, '2x 1.92 TB NVMe', HETZNER_COLOR),
              dedicatedServer('hetzner-ax162r', 'AX162-R', 134.90, 'AMD EPYC 9354P', 256, '2x 1.92 TB NVMe', HETZNER_COLOR),
              dedicatedServer('hetzner-ax162s', 'AX162-S', 134.90, 'AMD EPYC 9354P', 256, '2x 3.84 TB NVMe', HETZNER_COLOR),
            ],
          },
          {
            id: 'ex',
            label: 'EX-Line (Intel)',
            templates: [
              dedicatedServer('hetzner-ex44', 'EX44', 49.90, 'Intel Core i5-13500', 64, '2x 512 GB NVMe', HETZNER_COLOR),
              dedicatedServer('hetzner-ex63', 'EX63', 64.90, 'Intel Core i9-13900', 64, '2x 1 TB NVMe', HETZNER_COLOR),
              dedicatedServer('hetzner-ex130r', 'EX130-R', 119.90, 'Intel Xeon W-2445', 128, '2x 1.92 TB NVMe', HETZNER_COLOR),
              dedicatedServer('hetzner-ex130s', 'EX130-S', 119.90, 'Intel Xeon W-2445', 128, '2x 3.84 TB NVMe', HETZNER_COLOR),
            ],
          },
          {
            id: 'sx',
            label: 'SX-Line (Storage)',
            templates: [
              dedicatedServer('hetzner-sx65', 'SX65', 54.90, 'AMD Ryzen 5 3600', 64, '4x 16 TB HDD', HETZNER_COLOR),
              dedicatedServer('hetzner-sx135', 'SX135', 109.90, 'AMD Ryzen 5 3600', 64, '2x 14 TB HDD + 3x 18 TB HDD', HETZNER_COLOR),
              dedicatedServer('hetzner-sx295', 'SX295', 189.90, 'AMD EPYC 7282', 128, '4x 18 TB HDD + 2x 1.92 TB NVMe', HETZNER_COLOR),
            ],
          },
          {
            id: 'gpu',
            label: 'GPU-Line',
            templates: [
              gpuServer('hetzner-gex44', 'GEX44', 199.90, 'Intel Core i5-13500', 64, '2x 512 GB NVMe', 'NVIDIA RTX 4000 SFF', 20, HETZNER_COLOR),
              gpuServer('hetzner-gex131', 'GEX131', 379.90, 'Intel Xeon W-2445', 128, '2x 1.92 TB NVMe', 'NVIDIA L40S', 48, HETZNER_COLOR),
            ],
          },
        ],
      },
      {
        id: 'managed',
        label: 'Managed Server',
        templates: [
          managedServer('hetzner-mc30', 'MC30', 29.90, 4, 8, 200, HETZNER_COLOR),
          managedServer('hetzner-mc60', 'MC60', 59.90, 6, 16, 400, HETZNER_COLOR),
          managedServer('hetzner-ma80', 'MA80', 79.90, 8, 32, 500, HETZNER_COLOR),
          managedServer('hetzner-ma130', 'MA130', 129.90, 12, 64, 750, HETZNER_COLOR),
          managedServer('hetzner-ma200', 'MA200', 199.90, 16, 128, 1000, HETZNER_COLOR),
        ],
      },
      {
        id: 'storage',
        label: 'Storage',
        children: [
          {
            id: 'object-storage',
            label: 'Object Storage',
            templates: [
              storageProduct('hetzner-s3-1tb', 'S3 (1 TB)', 5.49, 1, '1 TB S3-kompatibler Object Storage', HETZNER_COLOR),
            ],
          },
          {
            id: 'storagebox',
            label: 'Storage Box',
            templates: [
              storageProduct('hetzner-bx11', 'BX11', 3.49, 1, '1 TB Storage Box (CIFS/FTP/SSH)', HETZNER_COLOR),
              storageProduct('hetzner-bx21', 'BX21', 6.49, 5, '5 TB Storage Box (CIFS/FTP/SSH)', HETZNER_COLOR),
              storageProduct('hetzner-bx31', 'BX31', 11.49, 10, '10 TB Storage Box (CIFS/FTP/SSH)', HETZNER_COLOR),
              storageProduct('hetzner-bx41', 'BX41', 18.49, 20, '20 TB Storage Box (CIFS/FTP/SSH)', HETZNER_COLOR),
            ],
          },
        ],
      },
    ],
  },
];

// ==========================================
// Search
// ==========================================

export interface CatalogSearchResult {
  template: ResourceTemplate;
  providerName: string;
  path: string[];
}

export function searchCatalogTemplates(query: string): CatalogSearchResult[] {
  const lower = query.toLowerCase().trim();
  if (!lower) return [];

  const results: CatalogSearchResult[] = [];

  for (const provider of RESOURCE_CATALOG) {
    function walkGroups(groups: CatalogGroup[], path: string[]) {
      for (const group of groups) {
        const currentPath = [...path, group.label];
        if (group.templates) {
          for (const t of group.templates) {
            if (
              t.name.toLowerCase().includes(lower) ||
              t.description.toLowerCase().includes(lower) ||
              t.category.toLowerCase().includes(lower)
            ) {
              results.push({ template: t, providerName: provider.name, path: currentPath });
            }
          }
        }
        if (group.children) {
          walkGroups(group.children, currentPath);
        }
      }
    }
    walkGroups(provider.groups, []);
  }

  return results;
}

// ==========================================
// Template → ResourceDefinition
// ==========================================

export function createResourceFromTemplate(
  template: ResourceTemplate,
  scope: ResourceDefinition['scope'],
): ResourceDefinition {
  const now = new Date().toISOString();
  const id = generateResourceId(template.name);

  const fields: ResourceField[] = template.fields.map((f, i) => ({
    id: `${id}-field-${i}`,
    key: f.key,
    value: f.value,
    type: f.type,
    unit: f.unit,
    interval: f.interval,
    aggregate: f.aggregate,
    showOnBadge: f.showOnBadge,
  }));

  return {
    id,
    name: template.name,
    category: template.category,
    scope,
    fields,
    color: template.color,
    createdAt: now,
    updatedAt: now,
  };
}
